import {
  Puzzle,
  Database,
  Route,
  Navigation,
  Wrench,
  Zap,
  Box,
  Cpu,
  ServerCog,
  PlayCircle,
} from 'lucide-react';
import { Badge } from '@reflex/ui/components/ui/badge';
import { IconSectionHeader } from '@/components/shared/IconSectionHeader';
import { MonoBadge } from '@/components/shared/MonoBadge';
import type { PluginManifest } from '@/lib/plugin-registry';
import { CollapsibleDetailRow } from './CollapsibleDetailRow';
import { LucideIconByName } from './LucideIconByName';

export function PluginCapabilitiesSection({ plugin }: { plugin: PluginManifest }) {
  const hooks = plugin.server?.hooks;
  const agentProvider = plugin.server?.agentProvider;
  const tables = plugin.server?.tables;
  const tableCount = plugin.server?.tableCount ?? 0;
  const migrations = plugin.server?.migrations;
  const migrationCount = plugin.server?.migrationCount ?? 0;
  const hasCapabilities =
    tableCount > 0 ||
    migrationCount > 0 ||
    !!hooks ||
    !!agentProvider ||
    (plugin.web?.routes?.length ?? 0) > 0 ||
    (plugin.web?.navItems?.length ?? 0) > 0;

  if (!hasCapabilities) return null;

  return (
    <section data-testid="plugin-detail-section-capabilities">
      <IconSectionHeader icon={Puzzle}>Capabilities</IconSectionHeader>
      <div className="space-y-2">
        {agentProvider && (
          <div className="flex items-baseline gap-2 text-sm">
            <span className="w-24 shrink-0 text-muted-foreground">Agent</span>
            <span className="flex items-center gap-1.5 text-foreground/80">
              <Cpu className="h-3.5 w-3.5 text-muted-foreground" />
              <MonoBadge>{agentProvider.type}</MonoBadge>
            </span>
          </div>
        )}
        {tableCount > 0 &&
          (tables && tables.length > 0 ? (
            <CollapsibleDetailRow
              label="DB tables"
              icon={Database}
              count={tableCount}
              noun="table"
              testId="plugin-capability-tables"
            >
              {tables.map((t) => (
                <div key={t.name} className="flex flex-wrap items-center gap-2 text-xs">
                  <MonoBadge>{t.name}</MonoBadge>
                  <span className="text-muted-foreground">
                    {t.columns.length} column{t.columns.length !== 1 ? 's' : ''}
                  </span>
                </div>
              ))}
            </CollapsibleDetailRow>
          ) : (
            <div className="flex items-baseline gap-2 text-sm">
              <span className="w-24 shrink-0 text-muted-foreground">DB tables</span>
              <span className="flex items-center gap-1.5 text-foreground/80">
                <Database className="h-3.5 w-3.5 text-muted-foreground" />
                {tableCount}
              </span>
            </div>
          ))}
        {migrationCount > 0 &&
          (migrations && migrations.length > 0 ? (
            <CollapsibleDetailRow
              label="Migrations"
              icon={Wrench}
              count={migrationCount}
              noun="migration"
              testId="plugin-capability-migrations"
            >
              {migrations.map((m) => (
                <div key={m} className="flex flex-wrap items-center gap-2 text-xs">
                  <MonoBadge>{m}</MonoBadge>
                </div>
              ))}
            </CollapsibleDetailRow>
          ) : (
            <div className="flex items-baseline gap-2 text-sm">
              <span className="w-24 shrink-0 text-muted-foreground">Migrations</span>
              <span className="flex items-center gap-1.5 text-foreground/80">
                <Wrench className="h-3.5 w-3.5 text-muted-foreground" />
                {migrationCount}
              </span>
            </div>
          ))}
        {hooks?.devboxSetupHooks && hooks.devboxSetupHooks.length > 0 && (
          <CollapsibleDetailRow
            label="Devbox setup"
            icon={Box}
            count={hooks.devboxSetupHooks.length}
            noun="hook"
            testId="plugin-capability-devbox-setup"
          >
            {hooks.devboxSetupHooks.map((h) => (
              <div key={h.step} className="flex flex-wrap items-center gap-2 text-xs">
                <MonoBadge>{h.step}</MonoBadge>
                <span className="text-muted-foreground">{h.label}</span>
              </div>
            ))}
          </CollapsibleDetailRow>
        )}
        {hooks?.devboxServices && hooks.devboxServices.length > 0 && (
          <CollapsibleDetailRow
            label="Devbox services"
            icon={ServerCog}
            count={hooks.devboxServices.length}
            noun="service"
            testId="plugin-capability-devbox-services"
          >
            {hooks.devboxServices.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-2 text-xs">
                <MonoBadge>{s.id}</MonoBadge>
                <span className="text-muted-foreground">{s.label}</span>
                {s.blocking && (
                  <Badge variant="outline" className="text-3xs font-normal">
                    blocking
                  </Badge>
                )}
              </div>
            ))}
          </CollapsibleDetailRow>
        )}
        {hooks?.devboxLaunchCommands && hooks.devboxLaunchCommands.length > 0 && (
          <CollapsibleDetailRow
            label="Launch commands"
            icon={PlayCircle}
            count={hooks.devboxLaunchCommands.length}
            noun="command"
            testId="plugin-capability-launch-commands"
          >
            {hooks.devboxLaunchCommands.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center gap-2 text-xs">
                <MonoBadge>{c.id}</MonoBadge>
                <span className="text-muted-foreground">{c.label}</span>
                {c.blocking && (
                  <Badge variant="outline" className="text-3xs font-normal">
                    blocking
                  </Badge>
                )}
              </div>
            ))}
          </CollapsibleDetailRow>
        )}
        {hooks?.lifecycleHooks && hooks.lifecycleHooks.length > 0 && (
          <div className="flex items-baseline gap-2 text-sm">
            <span className="w-24 shrink-0 text-muted-foreground">Lifecycle</span>
            <span className="flex items-center gap-1.5 text-foreground/80">
              <Zap className="h-3.5 w-3.5 text-muted-foreground" />
              {hooks.lifecycleHooks.some((h) => h.beforeTerminate) && (
                <Badge variant="secondary" className="text-xs font-normal">
                  beforeTerminate
                </Badge>
              )}
              {hooks.lifecycleHooks.some((h) => h.onTerminal) && (
                <Badge variant="secondary" className="text-xs font-normal">
                  onTerminal
                </Badge>
              )}
            </span>
          </div>
        )}
        {(plugin.web?.routes?.length ?? 0) > 0 && (
          <CollapsibleDetailRow
            label="Routes"
            icon={Route}
            count={plugin.web!.routes!.length}
            noun="route"
            testId="plugin-capability-routes"
          >
            {plugin.web!.routes!.map((r) => (
              <div key={r.path} className="flex flex-wrap items-center gap-2 text-xs">
                <MonoBadge>{r.path}</MonoBadge>
                <span className="font-mono text-muted-foreground">{r.componentPath}</span>
                {r.config && (
                  <Badge variant="outline" className="text-3xs font-normal">
                    has config
                  </Badge>
                )}
              </div>
            ))}
          </CollapsibleDetailRow>
        )}
        {(plugin.web?.navItems?.length ?? 0) > 0 && (
          <CollapsibleDetailRow
            label="Nav items"
            icon={Navigation}
            count={plugin.web!.navItems!.length}
            noun="item"
            testId="plugin-capability-nav-items"
          >
            {plugin.web!.navItems!.map((n) => (
              <div key={n.to} className="flex flex-wrap items-center gap-2 text-xs">
                <span className="font-medium">{n.label}</span>
                <MonoBadge>{n.to}</MonoBadge>
                <Badge variant="outline" className="text-3xs font-normal">
                  {n.group}
                </Badge>
                <LucideIconByName name={n.icon} className="h-3 w-3 text-muted-foreground" />
              </div>
            ))}
          </CollapsibleDetailRow>
        )}
      </div>
    </section>
  );
}
